import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ChevronRight, X, Phone, Mail } from "lucide-react";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } }
};

// Use the exact India map image provided by user
const INDIA_MAP_IMAGE = "https://customer-assets.emergentagent.com/job_skt-industry-leader/artifacts/caagz2ty_India-Blank-Outline-map.png";

// Interactive Map Section with actual India map image
const MapSection = () => {
  const [activeLocation, setActiveLocation] = useState(null);
  const [hoveredBranch, setHoveredBranch] = useState(null);

  // Branch locations with CORRECTED positions based on actual geography
  // Coordinates are percentages relative to map image dimensions
  const branchLocations = [
    { id: 0, name: "Bengaluru", type: "HQ", x: "34.5%", y: "70%", details: "Corporate Headquarters", state: "Karnataka", labelPos: "left", phones: ["+91-9342254010", "+91-9342254015"], email: "info@sktlogistics.com" },
    { id: 1, name: "Chennai", x: "43%", y: "68%", details: "South India Hub", state: "Tamil Nadu", labelPos: "right", phones: ["+91-9789020120"], email: "chennai@sktlogistics.com" },
    { id: 2, name: "Pune", x: "22%", y: "53%", details: "West India Operations", state: "Maharashtra", labelPos: "right", phones: ["+91-9503136542"], email: "pune@sktlogistics.com" },
    { id: 3, name: "Coimbatore", x: "32%", y: "76%", details: "Industrial Corridor Hub", state: "Tamil Nadu", labelPos: "left", phones: ["+91-9342254015"], email: "info@sktlogistics.com" },
    { id: 4, name: "Belgaum", x: "24.5%", y: "63%", details: "North Karnataka Hub", state: "Karnataka", labelPos: "right", phones: ["+91-9535195589"], email: "belgaum@sktlogistics.com" },
    { id: 5, name: "Pondicherry", x: "44%", y: "73%", details: "Coastal Operations", state: "Puducherry", labelPos: "right", phones: ["+91-9342254015"], email: "info@sktlogistics.com" },
    { id: 6, name: "Shimoga", x: "28%", y: "67%", details: "Central Karnataka Hub", state: "Karnataka", labelPos: "left", phones: ["+91-9342425242"], email: "info@sktlogistics.com" },
  ];

  // Highway routes connecting branches (SVG paths as percentages)
  const highwayRoutes = [
    { id: "nh44-blr-chn", from: 0, to: 1 },
    { id: "nh48-blr-pune", from: 0, to: 2 },
    { id: "nh44-blr-cbe", from: 0, to: 3 },
    { id: "nh48-blr-bgm", from: 0, to: 4 },
    { id: "nh32-blr-pdy", from: 0, to: 5 },
    { id: "nh48-blr-shm", from: 0, to: 6 },
    { id: "ecr-chn-pdy", from: 1, to: 5 },
  ];

  const getRouteActive = (route) => {
    return activeLocation === route.from || activeLocation === route.to || 
           hoveredBranch === route.from || hoveredBranch === route.to;
  };

  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white" id="locations">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.span variants={fadeInUp} className="inline-block mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-[#E63946]">
            Our Presence
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-[#0B192C] tracking-tight">
            Branch Network
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-slate-600 max-w-2xl mx-auto mt-4">
            Strategically positioned across South & West India to serve major industrial corridors
          </motion.p>
        </motion.div>

        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={fadeInUp}
          className="grid lg:grid-cols-5 gap-8 items-start"
        >
          {/* Branch List - Left Side */}
          <div className="lg:col-span-2 space-y-3 order-2 lg:order-1">
            {branchLocations.map((branch) => {
              const isOpen = activeLocation === branch.id || hoveredBranch === branch.id;
              return (
                <div
                  key={branch.id}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                    isOpen
                      ? 'border-[#E63946] bg-gradient-to-r from-white to-red-50 shadow-lg transform translate-x-2'
                      : 'border-slate-200 bg-white hover:border-[#E63946] hover:translate-x-2'
                  }`}
                  onMouseEnter={() => setHoveredBranch(branch.id)}
                  onMouseLeave={() => setHoveredBranch(null)}
                  onClick={() => setActiveLocation(activeLocation === branch.id ? null : branch.id)}
                  data-testid={`map-branch-${branch.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      branch.type === 'HQ' || isOpen
                        ? 'bg-[#E63946] text-white shadow-lg'
                        : 'bg-red-100 text-[#E63946]'
                    }`}>
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-[#0B192C]">{branch.name}</h4>
                        {branch.type === 'HQ' && (
                          <span className="px-2 py-0.5 bg-[#E63946] text-white text-xs font-bold rounded">HQ</span>
                        )}
                      </div>
                      <p className="text-slate-500 text-sm">{branch.details}</p>
                    </div>
                    <ChevronRight className={`w-5 h-5 transition-transform ${
                      activeLocation === branch.id ? 'rotate-90 text-[#E63946]' : 'text-slate-400'
                    }`} />
                  </div>

                  {activeLocation === branch.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.25 }}
                      className="mt-3 pt-3 border-t border-red-100 space-y-2 overflow-hidden"
                      data-testid={`branch-contact-${branch.id}`}
                    >
                      <div className="flex items-start gap-2 text-sm">
                        <Phone className="w-4 h-4 text-[#E63946] mt-0.5 flex-shrink-0" />
                        <div className="flex flex-col">
                          {branch.phones.map((p) => (
                            <a key={p} href={`tel:${p.replace(/\s|-/g, '')}`} className="text-slate-700 hover:text-[#E63946]">{p}</a>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-[#E63946] flex-shrink-0" />
                        <a href={`mailto:${branch.email}`} className="text-slate-700 hover:text-[#E63946] break-all">{branch.email}</a>
                      </div>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>

          {/* India Map - Right Side */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <div className="relative bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden" data-testid="interactive-map">
              {/* India Map Image */}
              <div className="relative">
                <img 
                  src={INDIA_MAP_IMAGE} 
                  alt="India Map" 
                  className="w-full h-auto"
                />
                
                {/* SVG Overlay for routes and markers */}
                <svg 
                  className="absolute inset-0 w-full h-full" 
                  viewBox="0 0 100 100" 
                  preserveAspectRatio="none"
                  style={{ pointerEvents: 'none' }}
                >
                  {/* Highway Routes */}
                  {highwayRoutes.map((route) => {
                    const fromBranch = branchLocations[route.from];
                    const toBranch = branchLocations[route.to];
                    const isActive = getRouteActive(route);
                    
                    return (
                      <line
                        key={route.id}
                        x1={fromBranch.x}
                        y1={fromBranch.y}
                        x2={toBranch.x}
                        y2={toBranch.y}
                        stroke={isActive ? "#E63946" : "#F97316"}
                        strokeWidth={isActive ? "0.5" : "0.3"}
                        strokeDasharray={isActive ? "0" : "1,0.5"}
                        opacity={isActive ? "1" : "0.6"}
                        style={{ transition: "all 0.3s ease" }}
                      />
                    );
                  })}
                </svg>

                {/* Branch Markers as absolute positioned divs */}
                {branchLocations.map((branch) => {
                  const isActive = activeLocation === branch.id || hoveredBranch === branch.id;
                  const isHQ = branch.type === 'HQ';
                  
                  return (
                    <div
                      key={branch.id}
                      className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                      style={{ left: branch.x, top: branch.y }}
                      onMouseEnter={() => setHoveredBranch(branch.id)}
                      onMouseLeave={() => setHoveredBranch(null)}
                      onClick={() => setActiveLocation(activeLocation === branch.id ? null : branch.id)}
                      data-testid={`map-marker-${branch.id}`}
                    >
                      {/* Pulse rings */}
                      <div className={`absolute inset-0 flex items-center justify-center ${isActive || isHQ ? 'animate-ping' : ''}`}>
                        <div className={`rounded-full bg-[#E63946] opacity-20 ${isHQ ? 'w-12 h-12' : 'w-10 h-10'}`}></div>
                      </div>
                      <div className={`absolute inset-0 flex items-center justify-center ${isActive ? 'animate-pulse' : ''}`}>
                        <div className={`rounded-full bg-[#E63946] opacity-30 ${isHQ ? 'w-8 h-8' : 'w-6 h-6'}`}></div>
                      </div>
                      
                      {/* Main marker */}
                      <div 
                        className={`relative flex items-center justify-center rounded-full bg-[#E63946] border-white shadow-lg transition-all duration-300 ${
                          isHQ ? 'w-6 h-6 border-[3px]' : isActive ? 'w-5 h-5 border-2' : 'w-4 h-4 border-2'
                        } ${isActive ? 'shadow-xl shadow-red-500/30' : ''}`}
                      >
                        {isHQ && <span className="text-white text-xs font-bold">★</span>}
                      </div>

                      {/* Label */}
                      <div 
                        className={`absolute top-1/2 -translate-y-1/2 whitespace-nowrap px-2 py-1 rounded text-xs font-semibold transition-all duration-200 ${
                          branch.labelPos === 'left' ? 'right-full mr-2' : 'left-full ml-2'
                        } ${
                          isActive 
                            ? 'bg-[#0B192C] text-white shadow-lg' 
                            : 'bg-white text-[#0B192C] border border-slate-200 shadow-sm'
                        } ${isActive || isHQ ? 'opacity-100' : 'opacity-80'}`}
                      >
                        {branch.name}{isHQ ? ' (HQ)' : ''}
                      </div>
                    </div>
                  );
                })}

                {/* Legend */}
                <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-slate-200">
                  <p className="text-xs font-bold text-[#0B192C] mb-2">Legend</p>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-[#E63946] border-2 border-white shadow"></div>
                      <span className="text-slate-600">Branch</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full bg-[#E63946] border-2 border-white shadow flex items-center justify-center">
                        <span className="text-white text-[8px]">★</span>
                      </div>
                      <span className="text-slate-600">HQ</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-6 h-0.5 bg-[#F97316]" style={{ backgroundImage: 'repeating-linear-gradient(90deg, #F97316 0, #F97316 4px, transparent 4px, transparent 8px)' }}></div>
                      <span className="text-slate-600">NH Route</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Branch Detail Card */}
              {activeLocation !== null && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute bottom-4 right-4 left-4 md:left-auto md:w-80 bg-[#0B192C] text-white p-4 rounded-xl shadow-2xl"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-[#E63946]" />
                        <h4 className="font-bold text-lg">{branchLocations[activeLocation].name}</h4>
                        {branchLocations[activeLocation].type === 'HQ' && (
                          <span className="px-2 py-0.5 bg-[#E63946] text-xs font-bold rounded">HQ</span>
                        )}
                      </div>
                      <p className="text-white/70 text-sm mt-1">
                        {branchLocations[activeLocation].details} • {branchLocations[activeLocation].state}
                      </p>
                      <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5">
                        <div className="flex items-start gap-2 text-sm">
                          <Phone className="w-4 h-4 text-[#E63946] mt-0.5 flex-shrink-0" />
                          <div className="flex flex-col">
                            {branchLocations[activeLocation].phones.map((p) => (
                              <a key={p} href={`tel:${p.replace(/\s|-/g, '')}`} className="text-white/90 hover:text-[#E63946]">{p}</a>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-[#E63946] flex-shrink-0" />
                          <a href={`mailto:${branchLocations[activeLocation].email}`} className="text-white/90 hover:text-[#E63946] break-all">
                            {branchLocations[activeLocation].email}
                          </a>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setActiveLocation(null)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MapSection;
